import dayjs from 'utils/dayjsConfig';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function parsePositiveIntegerFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const n = Number(String(value).trim());
    if (!Number.isInteger(n) || n <= 0) {
        return undefined;
    }
    return n;
}

/**
 * Converts a date value (Date, datetime-local string, or ISO string) to an ISO-8601 timestamp.
 *
 * @param {unknown} value
 * @returns {string | undefined}
 */
export function toIsoTimestamp(value) {
    if (!value) {
        return undefined;
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return undefined;
    }
    return parsed.format();
}

/**
 * Normalizes entity-filter TEXT values (string or string[]) to a single trimmed string.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeExternalSkuFilterValue(value) {
    const entries = Array.isArray(value) ? value : [value];
    const last = [...entries].reverse().find((entry) => entry != null && String(entry).trim() !== '');
    return last == null ? '' : String(last).trim();
}

/**
 * Validates entity-dropdown filter values and builds API filter params.
 *
 * @param {{
 *   externalSku?: string | string[],
 *   timestampRange?: { startDate?: Date | string, endDate?: Date | string },
 * }} [filterValues={}]
 * @returns {{ appliedFilters: { externalSku?: number, start?: string, end?: string }, error: string | null }}
 */
export function validateAndBuildAppliedFilters(filterValues = {}) {
    const externalSkuRaw = normalizeExternalSkuFilterValue(filterValues.externalSku);
    const externalSku = parsePositiveIntegerFilter(externalSkuRaw);
    if (externalSkuRaw && externalSku === undefined) {
        return { appliedFilters: {}, error: 'External SKU must be a positive integer' };
    }

    const startRaw = filterValues.timestampRange?.startDate;
    const endRaw = filterValues.timestampRange?.endDate;
    const hasStart = Boolean(startRaw);
    const hasEnd = Boolean(endRaw);
    if (hasStart !== hasEnd) {
        return { appliedFilters: {}, error: 'Start and end must be supplied together' };
    }

    if (!hasStart && !hasEnd) {
        return { appliedFilters: { externalSku }, error: null };
    }

    const start = toIsoTimestamp(startRaw);
    const end = toIsoTimestamp(endRaw);
    if (!start || !end) {
        return { appliedFilters: {}, error: 'Start and end must be valid dates' };
    }

    if (!dayjs(start).isBefore(dayjs(end))) {
        return { appliedFilters: {}, error: 'Start must be before end' };
    }

    return {
        appliedFilters: {
            externalSku,
            start,
            end,
        },
        error: null,
    };
}
